import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import i18n from '../i18n.js'
import { useProjectStore } from './projects.js'
import { useAuthStore } from './auth.js'
import { useLocalBackendStore, MIN_LOCAL_API_LEVEL } from './localBackend.js'
import { formatUsd } from '../utils/money.js'
import { connectionId } from '../realtime/liveConnection.js'

// Lo store non è un componente: le traduzioni arrivano dall'istanza globale.
const t = (key, params) => i18n.global.t(key, params)

// Battito del blocco. Il Backend considera abbandonato un blocco silenzioso da 3 minuti: 30 s
// lasciano margine per tre tentativi falliti.
//
// Le esecuzioni altrui non si interrogano piu' a intervalli: arrivano dall'hub. Il battito invece
// resta, e resta a 30 s: e' una scrittura, non una lettura, ed e' quello che tiene vivo il blocco
// anche quando l'hub e' giu' e quello che azzera la scadenza breve dopo una disconnessione.
const BEAT_MS = 30 * 1000

/**
 * Le esecuzioni delle verifiche AI vivono qui, non nel form: lo store dura quanto la pagina, il
 * componente no. È ciò che permette al funzionario di chiudere la finestra, aprire altri processi e
 * tornare a leggere diario e progresso.
 *
 * Confine noto: l'orchestrazione resta nel browser (i file li legge il LocalBackend sulla macchina
 * dell'utente), quindi una ricarica della pagina interrompe lo step in corso. Gli step già conclusi
 * sono salvati dal Backend nel processo (`aiProgress`) e si riprende da lì.
 */
export const useVerificationRunsStore = defineStore('verificationRuns', () => {
  // { [`${projectId}::${verificationId}`]: run }
  const runs = ref({})
  // Esecuzioni in corso di TUTTI gli utenti, dal registro del Backend.
  const activeLocks = ref([])

  // Fuori dalla reattività di proposito: gli AbortController e i timer non vanno proxati (chiamare
  // un metodo nativo attraverso un Proxy può fallire) e non servono al rendering.
  const aborts = new Map()
  const beats = new Map()

  const keyOf = (projectId, verificationId) => `${projectId}::${verificationId}`

  const get = (projectId, verificationId) => runs.value[keyOf(projectId, verificationId)] || null

  /** Esecuzioni avviate da questo browser e ancora in corso. */
  const activeRuns = computed(() => Object.values(runs.value).filter(r => r.status === 'running'))

  /** Blocco (di chiunque) su questa verifica, dal registro del Backend. */
  const lockFor = (projectId, verificationId) =>
    activeLocks.value.find(l => l.projectId === projectId && l.verificationId === verificationId) || null

  /** Blocco di un ALTRO utente: è quello che disabilita il pulsante. */
  const lockByOther = (projectId, verificationId) => {
    const lock = lockFor(projectId, verificationId)
    return lock && !lock.mine ? lock : null
  }

  // ---- helper puri, condivisi con il form ----

  // Gli step dichiarati in cfg.json. Verifica senza step = giro unico (comportamento storico).
  function stepsOf(v) {
    return Array.isArray(v?.steps) ? v.steps.filter(s => s && s.id) : []
  }

  // Uno step "superato": eseguito con esito positivo o liberato da un admin. Stessa regola del
  // Backend (ProjectService.IsStepPassed).
  function isStepPassed(status) {
    return status === 'ok' || status === 'override'
  }

  /**
   * Una liberazione gia' fatta non torna indietro per colpa di un processo letto prima di essa: il
   * documento in mano al form puo' essere di un istante precedente, e adottarlo cosi' com'e'
   * rimetterebbe lo step a `failed`, facendo ripartire la sequenza da quello appena liberato.
   * Si tiene l'`override` locale SOLO contro un `failed`: se lo step e' stato azzerato (riparti da
   * qui) arriva assente, e allora l'azzeramento vince, com'e' giusto.
   */
  function keepOverrides(incoming, local) {
    if (!local) return incoming
    for (const [id, entry] of Object.entries(local)) {
      if (entry?.status === 'override' && incoming[id]?.status === 'failed') incoming[id] = entry
    }
    return incoming
  }

  function stepStatus(run, stepId) {
    if (!run) return 'pending'
    if (run.status === 'running' && run.currentStepId === stepId) return 'running'
    return run.progress?.[stepId]?.status || 'pending'
  }

  function stepSummary(run, stepId) {
    return run?.progress?.[stepId]?.summary || ''
  }

  // Dati della liberazione, se lo step è stato sbloccato a mano da un admin.
  function stepOverride(run, stepId) {
    const entry = run?.progress?.[stepId]
    if (!entry || entry.status !== 'override') return null
    return { by: entry.overriddenBy || '?', note: entry.overrideNote || '' }
  }

  // "3/5" finché la procedura non è completa; null quando non c'è nulla da mostrare.
  function progressLabel(run) {
    const steps = run?.steps || []
    if (!steps.length) return null
    const done = steps.filter(s => isStepPassed(stepStatus(run, s.id))).length
    return done === 0 ? null : `${done}/${steps.length}`
  }

  function allStepsDone(run) {
    const steps = run?.steps || []
    return steps.length > 0 && steps.every(s => isStepPassed(stepStatus(run, s.id)))
  }

  // Lo step che la prossima pressione lancerebbe: il primo non ancora superato.
  function nextStep(run) {
    const steps = run?.steps || []
    if (!steps.length) return null
    return steps.find(s => !isStepPassed(stepStatus(run, s.id))) || steps[steps.length - 1]
  }

  // ---- limite di esecuzioni (maxRuns) ----

  // Chiave del contatore: le verifiche a giro unico non hanno step e stanno sotto '*'.
  const attemptKey = (step) => step?.id || '*'

  // Il limite dello step vince su quello della verifica; 0 o assente = illimitato.
  function attemptsMax(run, step) {
    const v = run?.config
    const raw = step && step.maxRuns !== undefined && step.maxRuns !== null ? step.maxRuns : v?.maxRuns
    const max = Number(raw)
    return Number.isFinite(max) && max > 0 ? max : 0
  }

  function attemptsUsed(run, step) {
    return run?.attempts?.[attemptKey(step)]?.count || 0
  }

  function attemptsLeft(run, step) {
    const max = attemptsMax(run, step)
    return max === 0 ? Infinity : Math.max(0, max - attemptsUsed(run, step))
  }

  // "1/3" accanto al pulsante quando un limite è configurato per il prossimo lancio.
  function attemptsLabel(run) {
    const step = nextStep(run)
    const max = attemptsMax(run, step)
    return max === 0 ? null : `${attemptsUsed(run, step)}/${max}`
  }

  const isAdmin = () => {
    const role = useAuthStore().user?.role
    return role === 'admin' || role === 'superadmin'
  }

  // Limite esaurito sullo step che partirebbe adesso. Admin e superadmin non sono mai bloccati.
  function limitReached(run) {
    if (!run || isAdmin()) return false
    if (run.steps.length && allStepsDone(run)) return false // niente da lanciare
    return attemptsLeft(run, nextStep(run)) <= 0
  }

  // ---- ciclo di vita di una voce ----

  /**
   * Prepara (o recupera) la voce di questa verifica leggendo l'avanzamento dal processo. Il Backend
   * è l'autorità: `aiProgress` e `aiRuns` arrivano dal documento, qui se ne tiene lo specchio.
   */
  function ensureRun(projectId, verification, project) {
    const key = keyOf(projectId, verification.id)
    const existing = runs.value[key]

    // Un'esecuzione viva non si tocca: sovrascriverla perderebbe diario e stato.
    if (existing && existing.status === 'running') return existing

    const progress = project?.aiProgress?.[verification.id]?.steps || existing?.progress || {}
    const attempts = project?.aiRuns?.[verification.id] || existing?.attempts || {}

    const run = existing || {
      key,
      projectId,
      verificationId: verification.id,
      status: 'idle',
      currentStepId: null,
      stepIndex: 0,
      log: [],
      liveText: '',
      thinking: '',
      trace: null,
      // Costo del giro corrente (= una riga AiUsage) così come arriva dal server: nessuna somma
      // di token lato client, il backend manda già il cumulativo dell'esecuzione.
      cost: null,
      // Totale sugli step: una verifica a 5 step sono 5 righe, e all'utente serve il totale.
      costTotal: 0,
      result: null,
      error: null,
      savedFiles: [],
      skipped: [],
      fieldUpdates: {},
      token: null,
      startedAt: null,
      finishedAt: null,
      blockedBy: null,
    }

    run.config = verification
    run.label = verification.label || verification.id
    run.icon = verification.icon || 'mdi-robot-outline'
    run.steps = stepsOf(verification)
    run.progress = keepOverrides({ ...progress }, existing?.progress)
    run.attempts = { ...attempts }

    runs.value[key] = run
    return run
  }

  // `detail` è il contenuto lungo dietro la riga (comando per intero, output di un tool): il
  // diario lo tiene ma lo mostra solo se il funzionario apre la riga.
  function logLine(run, kind, text, detail = null) {
    const ts = new Intl.DateTimeFormat('it-IT', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    }).format(new Date())
    run.log.push({ ts, kind, text, detail })
  }

  function traceText(evt) {
    const parts = []
    if (evt.model) parts.push(evt.model)
    if (evt.inputTokens != null || evt.outputTokens != null)
      parts.push(t('projects.verification.tokens', { in: evt.inputTokens ?? '–', out: evt.outputTokens ?? '–' }))
    if (evt.stopReason && evt.stopReason !== 'end_turn') parts.push('stop: ' + evt.stopReason)
    if (evt.info) parts.push(evt.info)
    // Il cumulativo dell'esecuzione su ogni riga: si vede dove è finito il denaro, non solo quanto.
    if (evt.runCostUsd != null) parts.push(formatUsd(evt.runCostUsd))
    return parts.join(' · ')
  }

  // Un giro di streaming: traduce i VerifyEvent in stato del run e ritorna l'esito.
  async function streamRound(run, runFn) {
    run.liveText = ''
    run.thinking = ''
    // Il costo che arriva è cumulativo sul GIRO (una riga AiUsage). Il totale della verifica è la
    // somma dei giri, quindi si parte da quanto c'era prima di questo step.
    const baseCost = run.costTotal || 0
    run.cost = null
    let resultData = null
    let errorMsg = null
    await runFn(evt => {
      // Prima dello switch: i totali viaggiano anche sull'evento `result`, non solo sui trace.
      if (evt.runCostUsd != null || evt.runCostSource) {
        run.cost = {
          usd: evt.runCostUsd,
          source: evt.runCostSource,
          requests: evt.runRequests,
          inputTokens: evt.runInputTokens,
          outputTokens: evt.runOutputTokens,
        }
        run.costTotal = baseCost + (evt.runCostUsd || 0)
      }
      switch (evt.type) {
        case 'phase':
          logLine(run, 'phase', evt.text, evt.detail)
          break
        case 'trace':
          // Due tipi di trace: quello dei token (aggiorna il riepilogo in alto) e le righe grigie
          // del diario (esiti dei tool), che non devono azzerare modello e token.
          if (evt.model || evt.inputTokens != null || evt.outputTokens != null) {
            run.trace = {
              model: evt.model, inputTokens: evt.inputTokens, outputTokens: evt.outputTokens,
              stopReason: evt.stopReason, info: evt.info
            }
          }
          logLine(run, 'trace', traceText(evt), evt.detail)
          break
        case 'thinking':
          run.thinking += evt.text
          break
        case 'delta':
          run.liveText += evt.text
          break
        case 'result':
          resultData = evt.data
          break
        case 'error':
          errorMsg = evt.message || 'Errore nello stream'
          break
      }
    })
    if (errorMsg) throw new Error(errorMsg)
    return resultData
  }

  // ---- specchi locali di ciò che il Backend ha scritto ----

  function recordStepResult(run, stepId, result) {
    run.progress = {
      ...run.progress,
      [stepId]: {
        status: result?.canProceed === true ? 'ok' : 'failed',
        verdict: result?.verdict || null,
        summary: result?.summary || '',
        at: new Date().toISOString()
      }
    }
  }

  // Il Backend ha appena consumato un tentativo: si allinea lo specchio senza ricaricare.
  // Vale anche per admin e superadmin: loro non vengono BLOCCATI dal limite, ma le loro esecuzioni
  // si contano come le altre — altrimenti il contatore non direbbe quante ne ha subite il processo.
  function recordAttempt(run, step) {
    if (attemptsMax(run, step) === 0) return
    const key = attemptKey(step)
    run.attempts = {
      ...run.attempts,
      [key]: { count: (run.attempts[key]?.count || 0) + 1, at: new Date().toISOString() }
    }
  }

  // I campi di validazione li scrive il Backend; qui si annota il valore perché il form, quando è
  // aperto, lo rifletta senza far risultare il campo "modificato".
  function noteField(run, field, value) {
    if (!field || value === undefined) return
    run.fieldUpdates = { ...run.fieldUpdates, [field]: value }
  }

  // Campi scritti dal Backend in un colpo solo (azioni tool): `fields` è { nome: valore }.
  function noteFields(run, fields) {
    if (!fields || typeof fields !== 'object') return
    for (const [field, value] of Object.entries(fields)) noteField(run, field, value)
  }

  // ---- selezione dei file (filtro per cartelle + impronte) ----

  // Cartelle dichiarate per lo step (che vince) o per la verifica. Vuoto = tutti i file.
  function foldersOf(v, step) {
    const raw = Array.isArray(step?.folders) && step.folders.length ? step.folders : v?.folders
    return Array.isArray(raw)
      ? raw.map(f => String(f || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')).filter(Boolean)
      : []
  }

  // Tiene solo i file dentro le cartelle previste, sottocartelle incluse. Il confronto è sul
  // `relativePath`: i documenti condivisi arrivano già prefissati col nome della loro cartella.
  function filterByFolders(files, folders) {
    if (!folders.length) return files
    const wanted = folders.map(f => f.toLowerCase())
    return files.filter(f => {
      const path = String(f.relativePath || f.name || '').replace(/\\/g, '/').toLowerCase()
      return wanted.some(w => path === w || path.startsWith(w + '/'))
    })
  }

  // Impronta del file: solo metadati dell'elenco, nessuna lettura. Il Backend ne ricava la data di
  // modifica. Senza `modifiedAt` (LocalBackend non aggiornato) non c'è impronta.
  function fingerprintOf(info) {
    if (info?.modifiedAt == null || info?.sizeBytes == null) return null
    return `${info.relativePath || info.name}|${info.sizeBytes}|${info.modifiedAt}`
  }

  // ---- motore backend ----

  // Una sola chiamata, senza triage: il loop gira nel Backend (DeepSeek). Se la verifica analizza i
  // file, tutti i file supportati (entro il budget) vengono passati: è l'agente a decidere cosa
  // leggere.
  async function runBackend(run, v, projectId, signal, stepId) {
    const projectStore = useProjectStore()
    const MAX_TOTAL_BYTES = 120 * 1024 * 1024
    const MAX_FILE_BYTES = 30 * 1024 * 1024
    // Anche i formati che solo uno script sa aprire sono utili: il Backend li estrae per il modello.
    const SUPPORTED = /\.(pdf|png|jpe?g|gif|webp|txt|md|log|csv|xml|json|html?|docx?|xlsx?|pptx?|msg|eml|rtf|odt|ods|zip)$/i

    const attachments = []
    if (v.analyzeFiles) {
      logLine(run, 'phase', t('projects.verification.readingFileList'))
      const projectFiles = await projectStore.listProjectFiles(projectId)
      // I file della cartella condivisa "Documentos" (fonti standard, es. beneficio ICMS) sono taggati
      // source:'documents' e col relativePath prefissato.
      const sharedDocs = await projectStore.listDocuments()
      const all = [...projectFiles, ...sharedDocs]
      const files = filterByFolders(all, v.folders || [])
      if (files.length !== all.length) {
        logLine(run, 'phase', t('projects.verification.filesFiltered', { kept: files.length, total: all.length }))
      }
      logLine(run, 'phase', t('projects.verification.filesInFolder', { count: files.length }))

      // Due limiti distinti: il singolo file troppo grande viene saltato senza consumare il budget,
      // così un allegato enorme non fa sparire tutti i file piccoli che lo seguono.
      let total = 0
      for (const info of files) {
        if (!SUPPORTED.test(info.name)) { run.skipped.push(t('projects.verification.skipUnsupported', { name: info.name })); continue }
        if ((info.sizeBytes || 0) > MAX_FILE_BYTES) {
          run.skipped.push(t('projects.verification.skipTooLarge', {
            name: info.name,
            mb: Math.round(MAX_FILE_BYTES / (1024 * 1024))
          }))
          continue
        }
        if (total + (info.sizeBytes || 0) > MAX_TOTAL_BYTES) { run.skipped.push(t('projects.verification.skipBudget', { name: info.name })); continue }

        // `path` conserva le sottocartelle: molte skill distinguono i documenti per cartella.
        // `fingerprint` serve al Backend per scegliere la copia più recente di un documento,
        // `shared` segna i file della cartella condivisa "Documentos".
        const path = info.relativePath || info.name
        const fingerprint = fingerprintOf(info)
        const shared = info.source === 'documents'
        logLine(run, 'phase', t('projects.verification.readingFile', { name: info.name }))
        const file = shared
          ? await projectStore.readDocument(info.readPath)
          : await projectStore.readProjectFile(projectId, info.relativePath)
        attachments.push({
          name: file.name, mimeType: file.mimeType, base64: file.base64, path, fingerprint, shared
        })
        total += info.sizeBytes || 0
      }
    }

    run.result = await streamRound(run, onEvent =>
      projectStore.verifyBackendStream(projectId, v.id, attachments, onEvent, signal, stepId, run.token))

    await saveProducedFiles(run, projectId)
  }

  // L'agente può restituire un percorso con i segmenti sanificati (accenti via, tutto il resto fuori
  // ASCII in "_"), per esempio «1- Docs para conferencia»: salvarlo alla lettera creerebbe una
  // cartella nuova accanto a quella vera, perché save-file crea le sottocartelle mancanti senza chiedere. Qui una cartella dell'agente
  // che non esiste, ma che sanificata coincide con una esistente, torna al nome reale del processo.
  function sanitizeSegment(s) {
    return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Za-z0-9 ._-]/g, '_').trim().toLowerCase()
  }

  const sanitizePath = p => String(p || '').replace(/\\/g, '/').split('/').map(sanitizeSegment).join('/')

  // Sanificato -> reale, per ogni cartella (e sottocartella) che il processo ha davvero.
  async function realFolderMap(projectId) {
    const projectStore = useProjectStore()
    const map = new Map()
    let files = []
    try { files = await projectStore.listProjectFiles(projectId) } catch { return map }
    for (const f of files) {
      const parts = String(f.relativePath || '').replace(/\\/g, '/').split('/')
      parts.pop()
      for (let i = 1; i <= parts.length; i++) {
        const real = parts.slice(0, i).join('/')
        if (real) map.set(sanitizePath(real), real)
      }
    }
    return map
  }

  // Cartella inesistente ma riconoscibile: si corregge. Cartella davvero nuova (`_relatorios` alla
  // prima esecuzione) non ha corrispondenza e passa intatta, come deve.
  function realPath(path, folders) {
    const parts = String(path || '').replace(/\\/g, '/').split('/')
    const name = parts.pop()
    if (!parts.length) return path
    const dir = parts.join('/')
    const real = folders.get(sanitizePath(dir))
    return real && real !== dir ? real + '/' + name : path
  }

  // Un LocalBackend sotto il livello 2 tronca il nome a 50 caratteri alla cieca, estensione
  // compresa: «Conferência Numerário - <id>.xlsx» arrivava sul disco senza «.xlsx». Con quelli
  // il nome si accorcia qui, tenendo estensione e id del processo (serve a cercare il file).
  const OLD_LOCAL_MAX_NAME = 50

  function fitFileName(name, max, keep) {
    if (name.length <= max) return name
    const dot = name.lastIndexOf('.')
    const ext = dot > 0 && name.length - dot <= 12 ? name.slice(dot) : ''
    const stem = name.slice(0, name.length - ext.length)
    const room = max - ext.length
    const at = keep ? stem.indexOf(keep) : -1
    if (at >= 0) {
      const after = stem.slice(at + keep.length)
      const headRoom = room - keep.length - after.length
      if (headRoom >= 0) {
        // Si accorcia il testo prima dell'id; lo spazio lo tiene separato dall'id
        const head = stem.slice(0, at).slice(0, Math.max(0, headRoom - 1)).replace(/[\s_-]+$/, '')
        return (head ? head + ' ' : '') + keep + after + ext
      }
    }
    return stem.slice(0, room).trimEnd() + ext
  }

  function fitPath(path, max, keep) {
    const parts = String(path || '').split('/')
    parts.push(fitFileName(parts.pop(), max, keep))
    return parts.join('/')
  }

  // I report prodotti dall'agente tornano nella cartella del processo: è così che lo step successivo
  // (e la Parte 5 della conferenza DUIMP) li ritrova.
  async function saveProducedFiles(run, projectId) {
    const projectStore = useProjectStore()
    const produced = run.result?.files || []
    const folders = produced.length ? await realFolderMap(projectId) : new Map()
    // Livello sconosciuto (LocalBackend non raggiunto): si accorcia lo stesso, è il caso sicuro
    const level = produced.length ? await useLocalBackendStore().ensureApiLevel() : null
    const shorten = level == null || level < MIN_LOCAL_API_LEVEL
    for (const f of produced) {
      // Un binario non inlineato (download fallito lato Backend) arriva senza content: non salvarlo vuoto.
      if (f.content == null) { run.skipped.push(t('projects.verification.saveError', { path: f.path })); continue }
      let destino = realPath(f.path, folders)
      if (shorten) {
        const fitted = fitPath(destino, OLD_LOCAL_MAX_NAME, projectId)
        if (fitted !== destino) {
          logLine(run, 'phase', t('projects.verification.nameShortened', { from: destino, to: fitted }))
          destino = fitted
        }
      }
      logLine(run, 'phase', t('projects.verification.savingFile', { path: destino }))
      try {
        const saved = await projectStore.saveProjectFile(projectId, destino, f.content, f.encoding || 'text')
        run.savedFiles.push(saved?.relativePath || destino)
      } catch {
        run.savedFiles.push(t('projects.verification.saveError', { path: destino }))
      }
    }
    logLine(run, 'phase', t('projects.verification.completed'))
  }

  // Un giro di esecuzione: allegati e cartelle sono quelli dello step, con fallback sulla verifica.
  // Il motore invece è solo dell'azione: "tool" chiama il servizio esterno, tutto il resto va al
  // motore backend (anche i valori storici "messages"/"managed").
  async function runStep(run, projectId, signal, step) {
    const projectStore = useProjectStore()
    const v = run.config
    const effective = {
      ...v,
      analyzeFiles: step?.analyzeFiles !== undefined ? step.analyzeFiles : v.analyzeFiles,
      folders: foldersOf(v, step)
    }
    const stepId = step?.id || null

    if (v.engine === 'tool') {
      // Nessun allegato e nessun modello: il Backend chiama il servizio e scrive i campi. Una
      // chiamata può però produrre un documento (l'extrato della DUIMP): arriva in `result.files[]`
      // nella stessa forma del motore IA, quindi si salva con la stessa strada.
      run.result = await streamRound(run, onEvent =>
        projectStore.runToolStream(projectId, v.id, onEvent, signal, run.token))
      if (run.result?.files?.length) await saveProducedFiles(run, projectId)
    } else {
      await runBackend(run, effective, projectId, signal, stepId)
    }
  }

  // ---- blocco: acquisizione, battito, rilascio ----

  async function acquireLock(run) {
    const projectStore = useProjectStore()
    try {
      const res = await projectStore.acquireVerificationLock(run.projectId, run.verificationId, run.label, connectionId())
      run.token = res?.token || null
      run.blockedBy = null
      return true
    } catch (err) {
      if (err?.response?.status === 409) {
        run.status = 'locked'
        run.blockedBy = err.response.data || { owner: '?' }
        logLine(run, 'phase', t('projects.verification.lockedBy', { user: run.blockedBy.owner }))
        return false
      }
      throw err
    }
  }

  /**
   * Un battito subito, senza aspettare il giro del timer: e' cosi' che lo step corrente arriva agli
   * altri utenti in un secondo invece che in trenta. A fuoco e dimentica di proposito, la sequenza
   * non deve fermarsi per un avviso.
   */
  function beatNow(run) {
    if (!run.token) return
    const projectStore = useProjectStore()
    projectStore.beatVerificationLock(
      run.projectId, run.verificationId, run.token,
      run.currentStepId, run.stepIndex, run.steps.length, connectionId()
    ).catch(() => { /* il timer ci riprova fra poco */ })
  }

  function startBeat(run) {
    stopBeat(run)
    const projectStore = useProjectStore()
    const timer = setInterval(async () => {
      try {
        await projectStore.beatVerificationLock(
          run.projectId, run.verificationId, run.token,
          run.currentStepId, run.stepIndex, run.steps.length, connectionId())
      } catch (err) {
        if (err?.response?.status !== 409) return // problema di rete passeggero: si ritenta al giro dopo
        // Blocco perso (scaduto o tolto da un admin): fermarsi è l'unica cosa corretta, proseguire
        // significherebbe girare in parallelo a un altro esecutore.
        logLine(run, 'phase', t('projects.verification.lockLost'))
        run.error = t('projects.verification.lockLost')
        aborts.get(run.key)?.abort()
      }
    }, BEAT_MS)
    beats.set(run.key, timer)
  }

  function stopBeat(run) {
    const timer = beats.get(run.key)
    if (timer) clearInterval(timer)
    beats.delete(run.key)
  }

  async function releaseLock(run, { keepalive = false } = {}) {
    if (!run.token) return
    const projectStore = useProjectStore()
    const token = run.token
    run.token = null
    try {
      await projectStore.releaseVerificationLock(run.projectId, run.verificationId, token, { keepalive })
    } catch {
      // Il blocco scade da solo dopo 3 minuti: un rilascio fallito non è un problema.
    }
  }

  // ---- avvio, sequenza, stop ----

  /**
   * Avvia (o riprende) la procedura. Prende il blocco, esegue gli step finché gli esiti sono
   * positivi e rilascia. Uno step negativo o un errore fermano la sequenza: il funzionario corregge
   * e ripreme, e si riparte da lì.
   */
  async function start(projectId, verification, project) {
    const run = ensureRun(projectId, verification, project)
    if (run.status === 'running') return run

    // Blocco non ottenibile: se è di un altro utente lo dice `acquireLock`, se è un problema di
    // rete non si parte comunque — meglio fermarsi che eseguire senza protezione.
    try {
      if (!(await acquireLock(run))) return run
    } catch (err) {
      run.status = "error"
      run.error = err?.message || t("projects.verification.error")
      return run
    }

    const abort = new AbortController()
    aborts.set(run.key, abort)

    run.status = 'running'
    run.error = null
    run.result = null
    run.savedFiles = []
    run.skipped = []
    run.log = []
    run.liveText = ''
    run.thinking = ''
    run.trace = null
    // Azzerato con il resto: senza, un rilancio si porterebbe dietro la spesa del tentativo prima.
    run.cost = null
    run.costTotal = 0
    run.startedAt = new Date().toISOString()
    run.finishedAt = null
    startBeat(run)

    const steps = run.steps
    const v = run.config

    try {
      if (!steps.length) {
        recordAttempt(run, null)
        await runStep(run, projectId, abort.signal, null)
        noteFields(run, run.result?.fields)
        noteField(run, v.validationField, run.result?.canProceed)
        return run
      }

      const startIndex = steps.findIndex(s => !isStepPassed(stepStatus(run, s.id)))
      if (startIndex > 0) {
        logLine(run, 'phase', t('projects.verification.resumingFrom', {
          step: steps[startIndex].label || steps[startIndex].id
        }))
      }

      for (let i = startIndex; i < steps.length; i++) {
        const step = steps[i]
        run.currentStepId = step.id
        run.stepIndex = i + 1
        beatNow(run) // gli altri devono vedere subito a che punto siamo
        logLine(run, 'phase', t('projects.verification.stepHeader', {
          n: i + 1, total: steps.length, label: step.label || step.id
        }))

        // Budget dello step esaurito: ci si ferma qui invece di farsi rifiutare dal Backend.
        if (!isAdmin() && attemptsLeft(run, step) <= 0) {
          logLine(run, 'phase', t('projects.verification.runLimitReached', { max: attemptsMax(run, step) }))
          break
        }
        recordAttempt(run, step)

        // Ogni step riparte pulito su esito e output: il diario invece resta, è la cronologia.
        run.result = null
        await runStep(run, projectId, abort.signal, step)

        const result = run.result
        recordStepResult(run, step.id, result)

        // Campo di validazione del singolo step (facoltativo): il Backend lo scrive alla fine di
        // OGNI step che lo dichiara, positivo o negativo.
        noteField(run, step.validationField, result?.canProceed)

        if (result?.canProceed !== true) {
          // Il gate si chiude subito: il Backend ha già scritto false per lo step negativo.
          if (result?.canProceed === false) noteField(run, v.validationField, false)
          logLine(run, 'phase', t('projects.verification.stepBlocked', { label: step.label || step.id }))
          break
        }

        // Il gate si apre solo a procedura conclusa: lo scrive il Backend sull'ultimo step.
        if (i === steps.length - 1) noteField(run, v.validationField, true)
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        logLine(run, 'phase', t('projects.verification.interrupted'))
        run.status = 'stopped'
      } else {
        run.error = err?.message || t('projects.verification.error')
        if (run.currentStepId) {
          recordStepResult(run, run.currentStepId, { canProceed: false, summary: run.error })
        }
      }
    } finally {
      stopBeat(run)
      await releaseLock(run)
      aborts.delete(run.key)
      run.currentStepId = null
      run.finishedAt = new Date().toISOString()
      run.status = run.error ? 'error' : (run.status === 'stopped' ? 'stopped' : 'done')
      // Il processo è cambiato (campi di validazione, aiProgress): si riallinea la lista anche se il
      // form è chiuso, così la tabella non mostra dati vecchi.
      // Il contatore locale scatta prima del lancio, ma il Backend restituisce il tentativo se il
      // modello non ha mai risposto: vale il suo numero, non quello dello specchio.
      refreshProject(projectId).then(fresh => {
        if (fresh && run.status !== 'running') run.attempts = { ...(fresh.aiRuns?.[run.verificationId] || {}) }
      })
      loadActiveLocks()
    }

    return run
  }

  function stop(projectId, verificationId) {
    const run = get(projectId, verificationId)
    if (!run) return
    aborts.get(run.key)?.abort()
  }

  /**
   * Copia del processo senza l'avanzamento di una verifica — tutta, o solo gli step elencati.
   * Serve perché `ensureRun` rilegge sempre `aiProgress` dal processo che riceve: passargli quello
   * vecchio dopo un azzeramento farebbe ricomparire gli step già superati.
   */
  function withoutProgress(project, verificationId, stepIds = null) {
    const all = project?.aiProgress
    if (!all?.[verificationId]) return project

    if (!stepIds?.length) {
      const { [verificationId]: _removed, ...rest } = all
      return { ...project, aiProgress: rest }
    }

    const steps = { ...(all[verificationId].steps || {}) }
    for (const id of stepIds) delete steps[id]
    return {
      ...project,
      aiProgress: { ...all, [verificationId]: { ...all[verificationId], steps } }
    }
  }

  /** "Ricomincia da capo": azzera l'avanzamento sul processo e riparte dal primo step. */
  async function restart(projectId, verification, project) {
    const projectStore = useProjectStore()
    const run = ensureRun(projectId, verification, project)
    if (run.status === 'running') return run

    let cleared
    try {
      cleared = await projectStore.clearVerificationProgress(projectId, verification.id)
    } catch (err) {
      run.error = err?.message || t('projects.verification.error')
      return run
    }
    run.progress = {}

    // `start` richiama `ensureRun`, che ricostruisce `run.progress` dal processo passato qui: con
    // l'oggetto del form, ancora quello di prima, l'azzeramento della riga sopra veniva disfatto
    // subito e la procedura ripartiva dal secondo step. La DELETE restituisce il processo
    // aggiornato; la voce si toglie comunque, così il rimedio non dipende da cosa risponde.
    return await start(projectId, verification, withoutProgress(cleared || project, verification.id))
  }

  /**
   * Riparte da UNO step: azzera quello scelto e tutti i successivi, lascia intatto quello che
   * viene prima. Serve mentre si tara una skill — rifare la Parte 1 senza buttare la Parte 2 non
   * avrebbe senso (il seguito lavora sul risultato di prima), ma rifare da metà in poi sì.
   *
   * I tentativi consumati NON tornano indietro, come per "ricomincia da capo": il limite conta i
   * lanci, e questi sono lanci.
   */
  async function restartFrom(projectId, verification, project, step) {
    const projectStore = useProjectStore()
    const run = ensureRun(projectId, verification, project)
    if (run.status === 'running') return run

    const from = run.steps.findIndex(s => s.id === step?.id)
    if (from < 0) return run
    const ids = run.steps.slice(from).map(s => s.id)

    let cleared
    try {
      cleared = await projectStore.clearVerificationProgress(projectId, verification.id, ids)
    } catch (err) {
      run.error = err?.message || t('projects.verification.error')
      return run
    }

    run.progress = { ...run.progress }
    for (const id of ids) delete run.progress[id]

    return await start(projectId, verification, withoutProgress(cleared || project, verification.id, ids))
  }

  /** Azzera i tentativi consumati (admin): l'unico modo di sbloccare un funzionario a limite pieno. */
  async function resetAttempts(projectId, verificationId) {
    const projectStore = useProjectStore()
    const run = get(projectId, verificationId)
    if (!run || run.status === 'running') return
    try {
      await projectStore.clearVerificationRuns(projectId, verificationId)
    } catch (err) {
      run.error = err?.message || t('projects.verification.error')
      return
    }
    run.attempts = {}
    logLine(run, 'phase', t('projects.verification.resetRunsDone'))
  }

  /** Libera uno step bloccato (admin): la sequenza ripartirà da quello dopo. */
  async function overrideStep(projectId, verificationId, step, note) {
    const projectStore = useProjectStore()
    const run = get(projectId, verificationId)
    if (!run) return

    const updated = await projectStore.overrideVerificationStep(projectId, verificationId, step.id, note)

    run.progress = {
      ...run.progress,
      [step.id]: {
        ...(run.progress[step.id] || {}),
        status: 'override',
        overriddenBy: updated?.aiProgress?.[verificationId]?.steps?.[step.id]?.overriddenBy || '',
        overrideNote: note
      }
    }

    // Il gate può essersi aperto (tutti gli step superati): si annotano i campi scritti dal Backend.
    const fields = [run.config?.validationField, ...run.steps.map(s => s.validationField)].filter(Boolean)
    for (const field of fields) {
      if (updated && field in updated) noteField(run, field, updated[field])
    }

    logLine(run, 'phase', t('projects.verification.overrideDone', { step: step.label || step.id, note }))
    return updated
  }

  /** Sblocco forzato di un'esecuzione altrui rimasta appesa (admin). */
  async function forceUnlock(projectId, verificationId) {
    const projectStore = useProjectStore()
    await projectStore.forceReleaseVerificationLock(projectId, verificationId)
    await loadActiveLocks()
  }

  async function refreshProject(projectId) {
    try {
      const projectStore = useProjectStore()
      const fresh = await projectStore.getProject(projectId)
      if (!fresh) return
      const list = projectStore.projects
      const i = list.findIndex(p => p.idProject === projectId)
      if (i >= 0) list[i] = fresh
      return fresh
    } catch {
      // Riallineamento di cortesia: se fallisce, basta un refresh della lista.
    }
    return null
  }

  // ---- registro delle esecuzioni di tutti ----

  async function loadActiveLocks() {
    try {
      const projectStore = useProjectStore()
      activeLocks.value = await projectStore.loadActiveVerificationLocks()
    } catch {
      // Registro non raggiungibile: si tiene l'ultimo elenco noto invece di far sparire tutto.
    }
  }

  /**
   * Avvio del registro. Il nome resta quello di prima, ma non c'e' piu' alcun intervallo: una
   * lettura iniziale e poi solo eventi dall'hub. Restano da registrare i gestori di pagina.
   */
  function startPolling() {
    loadActiveLocks()
    window.addEventListener('beforeunload', onBeforeUnload)
    window.addEventListener('pagehide', onPageHide)
    exposeToShell()
  }

  function stopPolling() {
    window.removeEventListener('beforeunload', onBeforeUnload)
    window.removeEventListener('pagehide', onPageHide)
    delete window.__lexActiveRuns
    delete window.__lexConfirmUnload
  }

  /**
   * Un evento `lockChanged` dall'hub. Prende il posto della lettura ogni venti secondi: prima un
   * collega vedeva partire una verifica con venti secondi di ritardo, ora entro un secondo.
   *
   * Il caso delicato e' l'ultimo: se a sparire e' un blocco NOSTRO mentre la sequenza gira ancora,
   * vuol dire che e' scaduto o che un admin lo ha tolto. Proseguire significherebbe girare in
   * parallelo a un altro esecutore, quindi ci si ferma, esattamente come fa il 409 del battito.
   */
  function applyLockEvent(event) {
    if (!event?.projectId || !event?.verificationId) return

    const authStore = useAuthStore()
    const me = authStore.user?.username
    const key = keyOf(event.projectId, event.verificationId)
    const i = activeLocks.value.findIndex(
      l => l.projectId === event.projectId && l.verificationId === event.verificationId)

    if (event.kind === 'acquired' || event.kind === 'beat') {
      // `mine` non puo' arrivare dal Backend: lo stesso messaggio va a tutti.
      const lock = { ...event, mine: event.owner === me }
      if (i >= 0) activeLocks.value[i] = lock
      else activeLocks.value.push(lock)
      return
    }

    if (i >= 0) activeLocks.value.splice(i, 1)

    if (event.kind === 'released') return

    const run = runs.value[key]
    if (event.owner === me && run?.status === 'running') {
      logLine(run, 'phase', t('projects.verification.lockLost'))
      run.error = t('projects.verification.lockLost')
      aborts.get(key)?.abort()
    }
  }

  // Ricaricare o chiudere la pagina interrompe l'esecuzione (la regia è qui nel browser): si avvisa.
  // Il blocco NON si rilascia qui: l'utente può ancora annullare, e la verifica proseguirebbe senza
  // blocco. Lo rilascia pagehide, che scatta solo quando la pagina se ne va davvero.
  let unloadConfirmed = false
  const onBeforeUnload = (e) => {
    if (unloadConfirmed || !activeRuns.value.length) return
    e.preventDefault()
    e.returnValue = ''
    return ''
  }

  // La pagina se ne va (ricarica confermata, navigazione, chiusura): blocco rilasciato subito invece
  // di restare appeso fino alla scadenza.
  const onPageHide = () => {
    for (const run of activeRuns.value) releaseLock(run, { keepalive: true })
  }

  // Per la shell desktop (LexSystem.exe): prima di "Ricarica" o "Esci" dal tray chiede se c'è una
  // verifica in corso e avvisa l'utente con un suo messaggio; se conferma, l'avviso del browser
  // sarebbe una seconda domanda uguale, quindi si salta.
  function exposeToShell() {
    window.__lexActiveRuns = () => activeRuns.value.length
    window.__lexConfirmUnload = () => { unloadConfirmed = true }
  }

  return {
    runs,
    activeLocks,
    activeRuns,
    get,
    lockFor,
    lockByOther,
    ensureRun,
    start,
    stop,
    restart,
    restartFrom,
    resetAttempts,
    overrideStep,
    forceUnlock,
    loadActiveLocks,
    applyLockEvent,
    startPolling,
    stopPolling,
    // helper condivisi con il form
    stepsOf,
    isStepPassed,
    stepStatus,
    stepSummary,
    stepOverride,
    progressLabel,
    allStepsDone,
    nextStep,
    attemptsMax,
    attemptsUsed,
    attemptsLeft,
    attemptsLabel,
    limitReached,
  }
})
